import { createContext, useContext, useEffect, useState, useRef } from "react";
import type { ReactNode } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
} from "firebase/auth";
import type { User } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, googleProvider, db } from "./firebase";
import { trackLogin, trackSignUp, trackLogout, identifyUser } from "./analytics";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  hasAcceptedTerms: boolean;
  acceptTerms: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const isNewSignUp = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      const previousUser = user;
      setUser(authUser);

      if (authUser) {
        // Check localStorage first (fast)
        const accepted = localStorage.getItem(`terms_accepted_${authUser.uid}`);
        if (accepted === "true") {
          setHasAcceptedTerms(true);
        } else {
          // Fallback: check Firestore (handles cross-device and cleared storage)
          try {
            const userStatsRef = doc(db, "userStats", authUser.uid);
            const userStatsDoc = await getDoc(userStatsRef);
            if (userStatsDoc.exists() && userStatsDoc.data()?.termsAcceptedAt) {
              localStorage.setItem(`terms_accepted_${authUser.uid}`, "true");
              setHasAcceptedTerms(true);
            } else {
              setHasAcceptedTerms(false);
            }
          } catch (error) {
            console.error("Failed to check terms acceptance:", error);
            setHasAcceptedTerms(false);
          }
        }

        // Track login/signup and identify user
        identifyUser(authUser.uid);

        // Determine if this is a new sign up or returning login
        if (!previousUser && isNewSignUp.current) {
          trackSignUp("google");
          isNewSignUp.current = false;
        } else if (!previousUser) {
          trackLogin("google");
        }
      } else {
        setHasAcceptedTerms(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []); // Subscribe once — onAuthStateChanged handles state changes

  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    // Check if user is new (created within last 5 seconds)
    const creationTime = result.user.metadata.creationTime;
    if (creationTime) {
      const createdAt = new Date(creationTime).getTime();
      const now = Date.now();
      if (now - createdAt < 5000) {
        isNewSignUp.current = true;
      }
    }
  };

  const signOut = async () => {
    trackLogout();
    await firebaseSignOut(auth);
    setHasAcceptedTerms(false);
  };

  const acceptTerms = () => {
    if (user) {
      // Set in localStorage immediately (for fast subsequent checks)
      localStorage.setItem(`terms_accepted_${user.uid}`, "true");
      setHasAcceptedTerms(true);

      // Persist to Firestore for legal compliance and cross-device sync
      const userStatsRef = doc(db, "userStats", user.uid);
      setDoc(userStatsRef, {
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: "2026-01",
      }, { merge: true }).catch((error) => {
        console.error("Failed to persist terms acceptance to Firestore:", error);
        // Don't block the user — localStorage is the primary check
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, signInWithGoogle, signOut, hasAcceptedTerms, acceptTerms }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
